<template>
  <v-data-iterator
    v-if="items.length > 0"
    :items="items"
    :items-per-page="itemsPerPage"
    :item-value="itemValue"
    v-model:page="currentPage"
  >
    <template v-slot:default="{ items: slotItems, isExpanded, toggleExpand }">
      <v-row>
        <v-col v-for="item in slotItems" :key="item.raw[itemValue]" cols="12">
          <v-card elevation="2" class="pa-2">
            <v-card-title class="d-flex align-center">
              <v-icon color="primary" icon="mdi-database" start></v-icon>
              <v-tooltip :text="item.raw.title" location="top">
                <template v-slot:activator="{ props }">
                  <h4 v-bind="props" class="truncate-title">{{ item.raw.title }}</h4>
                </template>
              </v-tooltip>
              <v-spacer></v-spacer>
              <v-tooltip
                v-if="hasRelevanceScore(item.raw.max_relevance_score)"
                :text="getRelevanceTooltip(item.raw.max_relevance_score)"
                location="top"
              >
                <template v-slot:activator="{ props }">
                  <v-chip
                    v-bind="props"
                    color="primary"
                    size="small"
                    variant="flat"
                    class="ml-4 mr-2 flex-shrink-0"
                  >
                    {{ formatRelevanceScore(item.raw.max_relevance_score) }}
                  </v-chip>
                </template>
              </v-tooltip>

              <slot name="save-action" :item="item.raw">
                <template v-if="saveAction">
                  <v-tooltip :text="$t('labels.save')">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        density="comfortable"
                        @click.stop="handleToggleSave(item.raw)"
                      >
                        <v-icon :color="getSaveColor(item.raw)">
                          {{ getSaveIcon(item.raw) }}
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>
              </slot>
            </v-card-title>

            <v-card-text>
              <p class="text-body-2">{{ truncateDescription(item.raw.description) }}</p>
            </v-card-text>

            <v-card-actions>
              <v-switch
                :label="`${isExpanded(item) ? $t('strings.hide-details') : $t('strings.show-details')}`"
                :model-value="isExpanded(item)"
                density="compact"
                inset
                color="primary"
                :hide-details="true"
                @click="() => toggleExpand(item)"
              ></v-switch>
              <v-spacer></v-spacer>
              <v-btn
                class="text-none"
                color="secondary"
                prepend-icon="mdi-open-in-new"
                variant="outlined"
                density="comfortable"
                :href="item.raw.dataset_url"
                target="_blank"
              >
                {{ $t('strings.view-dataset') }}
              </v-btn>
            </v-card-actions>

            <v-expand-transition>
              <div v-if="isExpanded(item)">
                <v-divider></v-divider>
                <v-list lines="two" density="compact">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-source-branch"></v-icon>
                    </template>
                    <v-list-item-title>{{ $t('labels.source') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">
                      <a
                        v-if="isValidUrl(item.raw.source)"
                        :href="item.raw.source"
                        target="_blank"
                        class="text-primary"
                      >
                        {{ item.raw.source }}
                      </a>
                      <span v-else>{{ item.raw.source }}</span>
                    </v-list-item-subtitle>
                  </v-list-item>

                  <v-list-item v-if="item.raw.license">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-license"></v-icon>
                    </template>
                    <v-list-item-title>{{ $t('labels.license') }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.raw.license }}</v-list-item-subtitle>
                  </v-list-item>

                  <v-list-item v-if="hasRelevanceScore(item.raw.max_relevance_score)">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-star"></v-icon>
                    </template>
                    <v-list-item-title>{{ $t('strings.relevance-score') }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatRelevanceScore(item.raw.max_relevance_score) }}
                    </v-list-item-subtitle>
                  </v-list-item>

                  <v-list-item :lines="false">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-text"></v-icon>
                    </template>
                    <v-list-item-title>{{ $t('labels.description') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-wrap text-body-2">
                      {{ item.raw.description }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>

                <v-divider></v-divider>
                <v-list :lines="false" density="compact">
                  <v-list-item v-if="item.raw.files">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-file"></v-icon>
                    </template>
                    <v-list-item-title
                      >{{ $t('strings.available-files') }} ({{ item.raw.files?.length || 0 }})
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="ml-8">
                    <v-virtual-scroll
                      v-if="item.raw.files && item.raw.files.length > 0"
                      :items="item.raw.files"
                      :height="64 * item.raw.files.length"
                      item-height="64"
                    >
                      <template v-slot:default="{ item: file }">
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :color="getFileIconColor(file.format)">
                              {{ getFileIcon(file.format) }}
                            </v-icon>
                          </template>

                          <v-list-item-title>{{ file.file_name }}</v-list-item-title>
                          <v-list-item-subtitle class="text-wrap">
                            {{ file.format }} - {{ formatFileSize(file.file_size) }}
                            <span class="text-caption">
                              &nbsp;[ {{ $t('labels.created-at') }}:
                              {{ formatDate(file.created) }} • {{ $t('labels.updated-at') }}:
                              {{ formatDate(file.modified) }} ]
                            </span>
                          </v-list-item-subtitle>

                          <template v-slot:append>
                            <v-menu location="bottom end">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-dots-vertical"
                                  density="comfortable"
                                  color="primary"
                                  variant="text"
                                  class="ml-2"
                                ></v-btn>
                              </template>
                              <v-list density="compact" nav>
                                <v-list-item
                                  v-if="isDetailsEnabled(file.format)"
                                  :disabled="detailsingFiles.has(file.file_url)"
                                  @click="openDetails(file, item.raw)"
                                >
                                  <template v-slot:prepend>
                                    <v-icon
                                      icon="mdi-file-eye-outline"
                                      color="primary"
                                      class="opacity-100"
                                    ></v-icon>
                                  </template>
                                  <v-list-item-title>{{ $t('labels.details') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                  v-if="isAddEnabled(file.format)"
                                  :disabled="downloadingFiles.has(file.file_url)"
                                  @click="openAddDialog(file, item.raw)"
                                >
                                  <template v-slot:prepend>
                                    <v-icon
                                      icon="mdi-file-plus-outline"
                                      color="primary"
                                      class="opacity-100"
                                    ></v-icon>
                                  </template>
                                  <v-list-item-title>{{ $t('labels.add') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                  :disabled="downloadingFiles.has(file.file_url)"
                                  @click="downloadFile(file.file_url, file.file_name)"
                                >
                                  <template v-slot:prepend>
                                    <v-icon
                                      icon="mdi-file-download-outline"
                                      color="primary"
                                      class="opacity-100"
                                    ></v-icon>
                                  </template>
                                  <v-list-item-title>{{ $t('labels.download') }}</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </template>
                        </v-list-item>
                      </template>
                    </v-virtual-scroll>

                    <v-alert v-else type="info" variant="text" density="compact" class="ma-4">
                      {{ $t('strings.no-files-available') }}
                    </v-alert>
                  </v-list-item>
                </v-list>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template v-slot:footer="{ pageCount }">
      <v-divider class="mt-4"></v-divider>
      <div class="d-flex align-center justify-center ga-4 pa-4 flex-wrap">
        <div class="text-caption">
          {{ items.length }} {{ $t('strings.results-found') }}
        </div>

        <div class="mx-4 text-caption">
          {{ $t('labels.page') }} {{ currentPage }} {{ $t('labels.of') }} {{ pageCount }}
        </div>

        <v-pagination
          v-model="currentPage"
          :length="pageCount"
          :total-visible="7"
          density="comfortable"
          rounded="circle"
        ></v-pagination>
      </div>
    </template>
  </v-data-iterator>

  <DetailsDialog
    v-model="detailsDialog"
    :loading="detailsLoading"
    :error="detailsError"
    :preview-data="detailsData"
    :preview-context="detailsContext"
    :file="detailsFile"
    :standalone="mfConfig.standaloneMode ?? false"
    @add="handleDetailsAdd"
    @download="handleDetailsDownload"
  />

  <AddDialog
    v-model="addDialog"
    :file="addFile"
    :dataset="addDataset"
    :datasources="effectiveDatasources"
    @confirm="handleAddConfirm"
  />

  <v-snackbar v-model="showDownloadError" :timeout="-1" color="warning" location="bottom">
    <div class="d-flex align-center">
      <v-icon start>mdi-alert-circle-outline</v-icon>
      {{ downloadAlertText || $t('strings.download-cors-error') }}
    </div>
    <template v-slot:actions>
      <v-btn variant="text" @click="showDownloadError = false"> OK </v-btn>
    </template>
  </v-snackbar>

  <v-snackbar v-model="showSuccessSnackbar" :timeout="3000" color="success" location="bottom">
    <div class="d-flex align-center">
      <v-icon start>mdi-check-circle-outline</v-icon>
      {{ successMessage }}
    </div>
    <template v-slot:actions>
      <v-btn variant="text" @click="showSuccessSnackbar = false"> OK </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { setError } from '@/mixins/events.js'
import axios from 'axios'
import api from '@/services/api'
import { getLocalizedRelevanceLabel, toNumericRelevance } from '@/services/relevance'
import DetailsDialog from '@/components/DetailsDialog.vue'
import AddDialog from '@/components/AddDialog.vue'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  itemsPerPage: {
    type: Number,
    default: 5,
  },
  itemValue: {
    type: String,
    default: 'dataset_url',
  },
  saveAction: {
    type: Boolean,
    default: true,
  },
  saveActionMode: {
    type: String,
    default: 'toggle',
  },
  isSaved: {
    type: Function,
    default: () => false,
  },
  datasources: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['toggle-save'])

const { t, locale } = useI18n()
const router = useRouter()

// Inject configuration from App.vue
const mfConfig = inject('mfConfig', {})
const realm = inject('realm', '')
const apiUrl = mfConfig?.api
const defaultFileFormatConfig = { icon: 'mdi-file-document', color: 'blue-grey' }
const opts = {
  headers: {},
  withCredentials: true,
}

const downloadingFiles = ref(new Set())
const showDownloadError = ref(false)
const downloadAlertText = ref('')
const detailsingFiles = ref(new Set())
const detailsDialog = ref(false)
const detailsLoading = ref(false)
const detailsData = ref(null)
const detailsError = ref(null)
const detailsContext = ref(null)
const detailsFile = ref(null)
const detailsDataset = ref(null)
const addDialog = ref(false)
const addFile = ref(null)
const addDataset = ref(null)
const currentPage = ref(1)
const fetchedDatasources = ref([])
const showSuccessSnackbar = ref(false)
const successMessage = ref('')

const effectiveDatasources = computed(() => {
  return props.datasources?.length ? props.datasources : fetchedDatasources.value
})

function handleToggleSave(item) {
  emit('toggle-save', item)
}

function getSaveIcon(item) {
  if (props.saveActionMode === 'toggle') {
    return props.isSaved(item) ? 'mdi-bookmark' : 'mdi-bookmark-outline'
  }
  return 'mdi-bookmark'
}

function getSaveColor(item) {
  if (props.saveActionMode === 'toggle') {
    return props.isSaved(item) ? 'secondary' : 'blue-grey'
  }
  return 'secondary'
}

function isValidUrl(string) {
  if (!string) return false
  try {
    new URL(string)
    return true
  } catch {
    return false
  }
}

function truncateDescription(description, maxLength = 200) {
  if (!description) return ''
  if (description.length <= maxLength) return description
  return description.substring(0, maxLength) + '...'
}

function hasRelevanceScore(value) {
  return value !== null && value !== undefined && value !== ''
}

function formatRelevanceScore(value) {
  if (!hasRelevanceScore(value)) return ''

  const localizedLabel = getLocalizedRelevanceLabel(value, t)
  if (localizedLabel) return localizedLabel

  const numericValue = toNumericRelevance(value)
  if (numericValue !== null) return numericValue.toFixed(1)

  return String(value)
}

function getRelevanceTooltip(value) {
  return t('strings.relevance-chip-tooltip', {
    value: formatRelevanceScore(value),
  })
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

function getFileIcon(format) {
  const config = getFileFormatConfig(format)
  return config?.icon || defaultFileFormatConfig.icon
}

function getFileIconColor(format) {
  const config = getFileFormatConfig(format)
  return config?.color || defaultFileFormatConfig.color
}

function getFileFormatConfig(format) {
  const normalizedFormat = format?.toUpperCase()
  const hasConfiguredFormats = mfConfig?.fileFormats && typeof mfConfig.fileFormats === 'object'

  if (!hasConfiguredFormats) {
    return defaultFileFormatConfig
  }

  if (!normalizedFormat) return null

  const selected = getConfigByFormat(mfConfig.fileFormats, normalizedFormat)
  if (!selected || typeof selected !== 'object') return null

  return {
    icon: selected.icon,
    color: selected.color,
  }
}

function getConfigByFormat(formatsConfig, normalizedFormat) {
  if (!formatsConfig || typeof formatsConfig !== 'object') return null

  const directMatch = formatsConfig[normalizedFormat]
  if (directMatch && typeof directMatch === 'object') return directMatch

  const key = Object.keys(formatsConfig).find((configKey) => {
    return configKey?.toUpperCase() === normalizedFormat
  })

  return key ? formatsConfig[key] : null
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale.value || 'en', {
    dateStyle: 'medium',
  }).format(date)
}

function isDetailsEnabled(format) {
  if (!format) return false
  const enabledFormats = ['CSV', 'JSON']
  return enabledFormats.includes(format.toUpperCase())
}

function isAddEnabled(format) {
  if (!format) return false
  const enabledFormats = ['CSV', 'JSON', 'XML', 'TEXT']
  // Check if standalone mode is enabled in the configuration, if so disable the add action
  const standalone = mfConfig?.standaloneMode ?? false
  return enabledFormats.includes(format.toUpperCase()) && !standalone
}

function normalizeFileUrl(url) {
  if (!url) return null
  let cleaned = url.trim()
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1)
  }
  cleaned = cleaned.replace(/(%22|%27)+$/i, '')
  return cleaned
}

function downloadFile(url, filename) {
  downloadingFiles.value.add(url)
  const cleanedUrl = normalizeFileUrl(url)
  if (!cleanedUrl) {
    downloadAlertText.value = t('strings.download-invalid-url')
    showDownloadError.value = true
    downloadingFiles.value.delete(url)
    return
  }

  fetch(cleanedUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Download failed')
      return response.blob()
    })
    .then((blob) => {
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    })
    .catch((err) => {
      console.error('Download failed:', err)
      const opened = window.open(cleanedUrl, '_blank')
      downloadAlertText.value = opened
        ? t('strings.download-fallback')
        : t('strings.download-open-failed')
      showDownloadError.value = true
    })
    .finally(() => {
      downloadingFiles.value.delete(url)
    })
}

async function openDetails(file, dataset) {
  if (!file?.file_url) return
  const rawUrl = file.file_url
  const cleanedUrl = normalizeFileUrl(rawUrl)
  if (!cleanedUrl) {
    showDownloadError.value = true
    return
  }

  detailsContext.value = {
    title: dataset?.title || '',
    fileName: file?.file_name || '',
    created: file?.created || null,
    modified: file?.modified || null,
  }
  detailsDialog.value = true
  detailsFile.value = file
  detailsDataset.value = dataset
  detailsLoading.value = true
  detailsError.value = null
  detailsData.value = null
  detailsingFiles.value.add(rawUrl)

  try {
    const response = await api.getFileDetails({
      url: cleanedUrl,
      source: dataset?.source || '',
      dataset_url: dataset?.dataset_url || '',
      format: file?.format?.toLowerCase() || ''
    })
    detailsData.value = response?.data?.analysis || null
    if (!detailsData.value) {
      detailsError.value = t('strings.no-details-available')
    }
  } catch (error) {
    detailsError.value =
      error?.response?.data?.message || error?.message || t('strings.details-failed')
    setError(error, false)
  } finally {
    detailsLoading.value = false
    detailsingFiles.value.delete(rawUrl)
  }
}

function handleDetailsDownload(file) {
  if (!file?.file_url) return
  downloadFile(file.file_url, file.file_name)
}

function handleDetailsAdd(file) {
  openAddDialog(file, detailsDataset.value)
}

function openAddDialog(file, dataset) {
  if (!file?.file_url) return
  addFile.value = file
  addDataset.value = dataset || null
  addDialog.value = true
  loadDatasources()
}

function handleAddConfirm(payload) {
  const dataset = {
    name: payload.file.file_name ?? '',
    url: payload.file.file_url ?? '',
    method: 'GET',
    parameters: {
      headers: [],
      query: [],
      path: [],
    },
  }
  if (payload.datasourceId == '') {
    createDatasource(payload, dataset)
  } else {
    if (payload.redirectToDatasources) {
      sessionStorage.setItem('add-new-dataset', JSON.stringify(dataset))
      routeToDatasource({ _id: payload.datasourceId })
    } else {
      updateDatasource(payload.datasourceId, dataset)
    }
  }
}

async function loadDatasources() {
  if (props.datasources?.length) return

  const loader = ref(false)

  if (!apiUrl || !realm) {
    console.warn('[Data Discovery] Missing apiUrl or realm for datasources API call')
    fetchedDatasources.value = []
    return
  }

  loader.value = true

  try {
    const response = await axios.get(`${apiUrl}/${realm}/services`, opts)
    fetchedDatasources.value = response.data || []
    fetchedDatasources.value.forEach((el) => {
      el['selectable'] = true
    })
    loader.value = false
  } catch (error) {
    console.error('Failed to load datasources:', error)
    setError(error, false)
    fetchedDatasources.value = []
    loader.value = false
  }
}

function createDatasource(data, dataset) {
  axios
    .post(
      `${apiUrl}/${realm}/services`,
      {
        name: data.datasourceName,
        description: data.datasourceDescription,
        tenant: realm,
      },
      opts,
    )
    .then((response) => {
      if (data.redirectToDatasources) {
        sessionStorage.setItem('add-new-dataset', JSON.stringify(dataset))
        successMessage.value = t('strings.datasource-created-success')
        showSuccessSnackbar.value = true
        routeToDatasource(response.data)
      } else {
        addDatasetToDatasource(response.data._id, dataset, true)
      }
    })
    .catch((error) => {
      setError(error, false)
    })
}

function addDatasetToDatasource(datasourceId, dataset, isNewDatasource = false) {
  axios
    .get(`${apiUrl}/${realm}/services/${datasourceId}`, opts)
    .then((response) => {
      const datasource = response.data
      if (!datasource.endpoints) {
        datasource.endpoints = []
      }
      datasource.endpoints.push(dataset)

      return axios.put(`${apiUrl}/${realm}/services/${datasourceId}`, datasource, opts)
    })
    .then(() => {
      successMessage.value = isNewDatasource
        ? t('strings.datasource-and-dataset-created-success')
        : t('strings.dataset-added-success')
      showSuccessSnackbar.value = true
    })
    .catch((error) => {
      if (error && error.response) {
        setError(error, false)
      } else {
        console.error('Error adding dataset:', error)
      }
    })
}

function updateDatasource(id, dataset) {
  addDatasetToDatasource(id, dataset, false)
}

function routeToDatasource(item) {
  const datasourceId = item._id
  router.push({
    path: `/datasource-details/${datasourceId}`,
  })
}
</script>

<style scoped>
.truncate-title {
  flex: 1 1 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
